// Derived numbers for the overview and the lists. Deliberately just numbers: an
// earlier version of this file computed a ranked "things that want you" list, which
// turned the home page into a nag. Staleness still exists as a FACT on a row (a deal
// that hasn't been touched in three weeks says so), which is information rather than
// pressure.

import { isCommitted, type Config } from './model'
import { entitiesOfKind, owedItems, stageOf, workStatusOf, type State } from './reducer'

/** Days between an ISO date/timestamp and now. */
export function daysSince(iso: string, now = Date.now()): number {
  if (!iso) return Infinity
  const t = new Date(iso.length <= 10 ? `${iso}T12:00:00Z` : iso).getTime()
  if (Number.isNaN(t)) return Infinity
  return Math.floor((now - t) / 86_400_000)
}

/** How long an open deal can go untouched before its row says so. */
export const STALE_DEAL_DAYS = 14

/**
 * And how long before it stops saying so in red.
 *
 * A warning that is always on is not a warning. On a demo seeded four months ago,
 * every open row read `109d cold` in critical red down the whole column, which says
 * "everything is wrong" when it means "nothing has happened here in a while". Past
 * this second threshold the row is DORMANT rather than going cold: it still shows
 * its age, in the quiet colour, because at that point the number is history rather
 * than a thing to do today.
 */
export const DORMANT_DEAL_DAYS = 60

/** Headline numbers for the top of the overview. */
export function rollup(state: State, config: Config) {
  const deals = entitiesOfKind(state, 'deal')
  const kindOf = (d: (typeof deals)[number]) =>
    config.stages.find((s) => s.key === stageOf(d, config.stages))?.kind
  const sum = (pred: (k: ReturnType<typeof kindOf>) => boolean) =>
    deals.filter((d) => pred(kindOf(d))).reduce((acc, d) => acc + (Number(d.fields.value) || 0), 0)
  const count = (pred: (k: ReturnType<typeof kindOf>) => boolean) =>
    deals.filter((d) => pred(kindOf(d))).length

  return {
    // In the pipeline: might happen.
    open: sum((k) => k === 'open'),
    openCount: count((k) => k === 'open'),
    // Signed: committed money, work either starting or running.
    committed: sum(isCommitted),
    committedCount: count(isCommitted),
    // Finished.
    delivered: sum((k) => k === 'closed'),
    deliveredCount: count((k) => k === 'closed'),
    /** Work a signed deal is currently paying for — the honest version of "active". */
    liveWork: entitiesOfKind(state, 'project').filter(
      (p) => workStatusOf(state, p.id, config) === 'delivery',
    ).length,
    /** Dated promises not yet delivered, anywhere. */
    owed: owedItems(state).length,
  }
}
